(function (host, factory) {
  const adapter = factory();
  if (typeof module === 'object' && module.exports) module.exports = adapter;
  else if (host.document && !host.rafinedCartPenShades) {
    host.rafinedCartPenShades = adapter.mount(host);
  }
})(typeof window === 'object' ? window : globalThis, function () {
  'use strict';

  const PRODUCT_ID = 8789006614665;
  const HANDLE = 'brow-rafiner';
  const GROUP_PROPERTIES = /(?:^|_)bundle(?:_|$)|(?:^|_)gift(?:_|$)|kaching_cart/i;
  const properties = item => item.properties && typeof item.properties === 'object' ? item.properties : {};
  const planId = item => item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null;
  const stable = value => JSON.stringify(Object.keys(value).sort().map(key => [key, value[key]]));
  const identity = item => [String(item.product_id), String(item.variant_id || item.id), String(planId(item) || ''), stable(properties(item))].join('|');
  const matching = (cart, fingerprint) => (cart?.items || []).filter(item => identity(item) === fingerprint);
  const count = (cart, fingerprint) => matching(cart, fingerprint).reduce((sum, item) => sum + item.quantity, 0);
  function componentMetadata(item) {
    const raw = properties(item).__kaching_bundles;
    if (!raw) return false;
    try {
      const decoded = String(raw).trim().startsWith('{') ? String(raw) : atob(String(raw));
      const data = JSON.parse(decoded);
      return !!(data.bundleProduct || data.gift || data.upsell || data.parent);
    } catch (error) { return true; }
  }
  const ordinaryPen = item => !!item && Number(item.product_id) === PRODUCT_ID &&
    !item.kachingBundle && !item.customBundle && !item.parent_relationship &&
    !(item.item_components?.length) && !item.gift_card && item.final_price !== 0 && !componentMetadata(item) &&
    !Object.keys(properties(item)).some(key => key !== '__kaching_bundles' && GROUP_PROPERTIES.test(key));
  const multiPen = item => ordinaryPen(item) && Number.isInteger(item.quantity) && item.quantity > 1;
  const supportsPlan = (variant, sellingPlan) => !sellingPlan || !!variant.selling_plan_allocations?.some(allocation => String(allocation.selling_plan_id) === String(sellingPlan));
  const choicesFor = (item, variants) => planId(item)
    ? variants.filter(variant => variant.available && supportsPlan(variant, planId(item)))
    : variants;
  function eligible(item, variants = []) {
    if (!multiPen(item)) return false;
    if (!planId(item)) return true;
    const choices = choicesFor(item, variants);
    return choices.some(variant => String(variant.id) === String(item.variant_id)) &&
      choices.some(variant => String(variant.id) !== String(item.variant_id));
  }

  function findLine(cart, fingerprint, preferredKey) {
    const rows = matching(cart, fingerprint);
    return rows.find(item => item.key === preferredKey) || (rows.length === 1 ? rows[0] : null);
  }

  function makePlan(cart, request, variants) {
    const originalIdentity = request.fingerprint;
    const source = findLine(cart, originalIdentity, request.key);
    if (!source || !ordinaryPen(source) || source.quantity < 2 ||
      count(cart, originalIdentity) !== request.quantity) throw new Error('Your cart changed. Please choose the shade again.');
    const variant = variants.find(item => String(item.id) === String(request.variantId));
    if (!variant?.available) throw new Error('That shade is currently unavailable.');
    if (String(source.variant_id) === String(variant.id)) return null;
    const sellingPlan = planId(source);
    if (!supportsPlan(variant, sellingPlan)) {
      throw new Error('That shade is unavailable with this delivery plan.');
    }
    const target = { ...source, variant_id: variant.id };
    const addition = { id: variant.id, quantity: 1, properties: { ...properties(source) } };
    if (sellingPlan) addition.selling_plan = sellingPlan;
    return {
      sourceIdentity: originalIdentity,
      targetIdentity: identity(target),
      sourceKey: source.key,
      sourceCount: count(cart, originalIdentity),
      targetCount: count(cart, identity(target)),
      addition
    };
  }

  function stateOf(cart, plan) {
    const source = count(cart, plan.sourceIdentity);
    const target = count(cart, plan.targetIdentity);
    if (source === plan.sourceCount - 1 && target === plan.targetCount + 1) return 'changed';
    if (source === plan.sourceCount && target === plan.targetCount + 1) return 'added';
    if (source === plan.sourceCount && target === plan.targetCount) return 'original';
    return 'conflict';
  }

  const isCart = value => Array.isArray(value?.items) && Number.isInteger(value.item_count) &&
    value.item_count === value.items.reduce((total, item) => total + item.quantity, 0);
  function penState(cart) {
    if (!isCart(cart)) return null;
    const quantities = new Map();
    cart.items.filter(item => Number(item.product_id) === PRODUCT_ID).forEach(item => {
      const key = identity(item);
      quantities.set(key, (quantities.get(key) || 0) + item.quantity);
    });
    return JSON.stringify([...quantities].sort(([left], [right]) => left.localeCompare(right)));
  }

  async function synchronizeCart(api, expected, read) {
    const expectedState = penState(expected);
    if (expectedState === null) throw new Error('The cart did not return a complete response.');
    // Preserve the storefront-tested native refresh sequence before reading the authoritative cart.
    await api.refreshCart();
    await api.updateTieredPromotionsBar?.();
    const current = await read();
    if (penState(current) !== expectedState) throw new Error('The pen quantities or details changed while refreshing. Check your cart before checkout.');
    return current;
  }

  function nativePenRows(info) {
    const rows = [...info.children].filter(child => child.matches('.kaching-variant-selectors-row'));
    return rows.length > 1 && rows.every(row => Number(row.dataset.productId) === PRODUCT_ID &&
      row.querySelectorAll('select').length === 1) ? rows : [];
  }

  async function replaceOne(io, request, variants) {
    let cart;
    let plan;
    const trace = (stage, details) => { try { io.trace?.(stage, details); } catch (error) {} };
    try {
      cart = await io.read();
      plan = makePlan(cart, request, variants);
    } catch (error) {
      return { status: 'unchanged', cart, message: error.message };
    }
    if (!plan) return { status: 'unchanged', cart, message: '' };
    const traceState = stage => trace(stage, { state: stateOf(cart, plan), source: count(cart, plan.sourceIdentity), target: count(cart, plan.targetIdentity) });
    traceState('preflight');
    let mutationError;
    let addedKey;
    try {
      const added = await io.add(plan.addition);
      addedKey = added?.items?.[0]?.key || added?.key;
    } catch (error) {
      mutationError = error;
    }
    try {
      cart = await io.read();
    } catch (error) {
      return { status: 'needs-review', message: 'We could not confirm the shade change. Refresh and check your cart quantities before checkout.' };
    }
    traceState('post-add');
    if (mutationError && mutationError.definitive !== true) {
      return { status: 'needs-review', cart, message: 'The shade request was interrupted and may still complete. Refresh and check your cart quantities before checkout.' };
    }
    if (stateOf(cart, plan) === 'original') {
      return { status: 'unchanged', cart, message: mutationError?.message || 'That shade could not be added. Your original pens are still in your cart.' };
    }
    if (stateOf(cart, plan) !== 'added') return { status: 'needs-review', cart, message: 'Your cart changed during the shade update. Check your quantities before checkout.' };

    if (!mutationError) {
      const source = findLine(cart, plan.sourceIdentity, plan.sourceKey);
      if (source) {
        let changed;
        trace('decrement-start', { quantity: source.quantity - 1 });
        try {
          changed = await io.change(source.key, source.quantity - 1);
        } catch (error) {
          mutationError = error;
          trace('decrement-error', { definitive: error.definitive === true });
        }
        try {
          cart = isCart(changed) ? changed : await io.read();
        } catch (error) {
          return { status: 'needs-review', message: 'We could not confirm the shade change. Refresh and check your cart quantities before checkout.' };
        }
        traceState('decrement-result');
        if (stateOf(cart, plan) === 'changed') return { status: 'changed', cart, message: 'Shade updated.' };
        if (mutationError && mutationError.definitive !== true) {
          return { status: 'needs-review', cart, message: 'The shade request was interrupted and may still complete. Refresh and check your cart quantities before checkout.' };
        }
      }
    }

    if (stateOf(cart, plan) === 'original') return { status: 'reverted', cart, message: 'The shade change could not finish. Your original pens are still in your cart.' };
    if (stateOf(cart, plan) !== 'added') return { status: 'needs-review', cart, message: 'Your cart changed during the shade update. Check your quantities before checkout.' };
    const addedLine = findLine(cart, plan.targetIdentity, addedKey);
    if (!addedLine) return { status: 'needs-review', cart, message: 'The shade change could not finish. Check your cart for an extra pen before checkout.' };
    let reverted;
    try {
      reverted = await io.change(addedLine.key, addedLine.quantity - 1);
    } catch (error) {
      mutationError = error;
    }
    try {
      cart = isCart(reverted) ? reverted : await io.read();
    } catch (error) {
      return { status: 'needs-review', message: 'We could not confirm the shade change. Refresh and check your cart quantities before checkout.' };
    }
    return stateOf(cart, plan) === 'original'
      ? { status: 'reverted', cart, message: 'The shade change could not finish. Your original pens are still in your cart.' }
      : { status: 'needs-review', cart, message: 'The shade change could not finish. Check your cart for an extra pen before checkout.' };
  }

  function createQueue() {
    let tail = Promise.resolve();
    return operation => {
      const result = tail.then(operation);
      tail = result.catch(() => {});
      return result;
    };
  }

  function shouldBlockInteraction(state) {
    if (state.keyboardNavigation) return false;
    return !!((state.busy && ((state.inDrawer && !state.close) || state.cartControl)) ||
      ((state.busy || state.reviewRequired) && state.checkout) ||
      (state.checkoutPending && state.penSelect));
  }

  function mount(win) {
    const doc = win.document;
    const enqueue = createQueue();
    const rowsSelector = '#kaching-cart .kaching-cart-item[data-line-key]';
    const nativeSelector = '.kaching-cart-item__variant-selectors';
    const nativeBusy = row => row.matches('.kaching-cart__disabled') || !!row.querySelector(`${nativeSelector} select:disabled`);
    let cart;
    let variants;
    let productPromise;
    let busy = false;
    let checkoutPending = false;
    let reviewRequired = false;
    let scheduled = false;
    let message = '';
    let checkoutApi;
    let checkoutGuard;
    let stopped = false;

    const route = path => `${win.Shopify?.routes?.root || '/'}${path}`;
    async function request(path, body) {
      const controller = new win.AbortController();
      const timer = win.setTimeout(() => controller.abort(), 12000);
      try {
      const response = await win.fetch(route(path + (body ? '?kaching_cart_ignore=true' : '')), {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {})
      });
      let result;
      try { result = await response.json(); } catch (error) { throw new Error('The cart did not return a valid response.'); }
      if (!response.ok) {
        const error = new Error(typeof result.description === 'string' ? result.description : 'Your cart could not be updated.');
        error.definitive = response.status >= 400 && response.status < 500 && response.status !== 408;
        throw error;
      }
      return result;
      } finally { win.clearTimeout(timer); }
    }
    const io = {
      read: () => request('cart.js'),
      add: item => request('cart/add.js', { items: [item] }),
      change: (key, quantity) => request('cart/change.js', { id: key, quantity }),
      trace: (stage, details) => win.console?.info?.('[RAFINED pen]', stage, details)
    };
    function loadProduct() {
      if (!productPromise) productPromise = request(`products/${HANDLE}.js`).then(product => {
        if (Number(product.id) !== PRODUCT_ID || !Array.isArray(product.variants)) throw new Error('Product unavailable');
        variants = product.variants;
        schedule();
      }).catch(() => { productPromise = null; });
      return productPromise;
    }
    function announce(text, needsReview = false) {
      message = text;
      reviewRequired = needsReview;
      renderMessage();
    }
    function renderMessage() {
      const drawer = doc.querySelector('#kaching-cart');
      if (!drawer) return;
      let status = drawer.querySelector('[data-rf-pen-status]');
      if (!status) {
        status = doc.createElement('div');
        status.className = 'rf-cart-pen-status';
        status.setAttribute('data-rf-pen-status', '');
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        const text = doc.createElement('span');
        status.append(text);
        const button = doc.createElement('button');
        button.type = 'button';
        button.textContent = 'Review updated cart';
        button.addEventListener('click', async () => {
          if (busy) return;
          setBusy(true);
          try {
            cart = await io.read();
            cart = await synchronizeCart(win.kachingCartApi, cart, io.read);
            announce('Cart refreshed. Check the quantities and shades before checkout.');
          } catch (error) {
            announce('Your cart could not be refreshed. Please try again.', true);
          } finally { setBusy(false); schedule(); }
        });
        status.append(button);
        (drawer.querySelector('.kaching-cart__contents') || drawer.querySelector('kaching-cart-drawer') || drawer).append(status);
      }
      if (status.firstElementChild.textContent !== message) status.firstElementChild.textContent = message;
      status.lastElementChild.hidden = !reviewRequired;
      status.lastElementChild.disabled = busy;
      status.toggleAttribute('data-rf-pen-status-quiet', !reviewRequired && (message === 'Updating shade…' || message === 'Shade updated.'));
      status.hidden = !message;
    }
    function installCheckoutGuard() {
      const api = win.kachingCartApi;
      if (!api?.getCheckoutButtonClickHandler || !api.setCheckoutButtonClickHandler) return false;
      if (api !== checkoutApi || api.getCheckoutButtonClickHandler() !== checkoutGuard) {
        const previous = api.getCheckoutButtonClickHandler();
        checkoutGuard = async event => {
          if (busy || reviewRequired) return false;
          checkoutPending = true;
          schedule();
          try {
            const result = previous ? await previous(event) : true;
            if (result === false || busy || reviewRequired) {
              checkoutPending = false;
              schedule();
              return false;
            }
            return true;
          } catch (error) {
            checkoutPending = false;
            announce('Checkout could not start. Please try again.');
            schedule();
            return false;
          }
        };
        api.setCheckoutButtonClickHandler(checkoutGuard);
        checkoutApi = api;
      }
      return true;
    }
    function setBusy(value) {
      busy = value;
      const drawer = doc.querySelector('#kaching-cart');
      if (drawer) {
        drawer.toggleAttribute('data-rf-pen-busy', value);
        if (value) drawer.setAttribute('aria-busy', 'true');
        else drawer.removeAttribute('aria-busy');
      }
      doc.querySelectorAll('[data-rf-pen-select]').forEach(select => { select.disabled = value || reviewRequired || checkoutPending; });
      renderMessage();
    }
    function isCheckout(target) {
      return !!target?.closest?.('[name="checkout"],a[href*="/checkout"],[class*="checkout-button"],shopify-accelerated-checkout,shopify-accelerated-checkout-cart');
    }
    function guardInteraction(event) {
      const target = event.target;
      const inDrawer = target?.closest?.('#kaching-cart');
      const close = target?.closest?.('.kaching-cart__close');
      const keyboardAllowed = event.type === 'keydown' && ['Escape', 'Tab'].includes(event.key);
      const cartControl = target?.closest?.('form[action*="/cart/add"],form[action$="/cart"],form[action$="/cart/"],[name="add"],[data-kit-add],[data-cta],[data-sticky-add],[data-add-to-cart],a[href*="/cart/add"],.shopify-payment-button');
      if (shouldBlockInteraction({ busy, reviewRequired, checkoutPending, inDrawer, close, keyboardNavigation: keyboardAllowed,
        checkout: isCheckout(target), cartControl, penSelect: target?.closest?.('[data-rf-pen-select]') })) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }
    async function changeShade(select, item) {
      const variantId = Number(select.value);
      if (String(variantId) === String(item.variant_id)) return;
      const mutation = { key: item.key, fingerprint: identity(item), quantity: count(cart, identity(item)), variantId };
      setBusy(true);
      announce('Updating shade…');
      await enqueue(async () => {
        let outcome;
        try {
          outcome = await replaceOne(io, mutation, variants);
          io.trace('transaction-result', { status: outcome.status, itemCount: outcome.cart?.item_count });
          cart = outcome.cart || cart;
          cart = await synchronizeCart(win.kachingCartApi, cart, io.read);
          io.trace('native-sync-complete', { itemCount: cart.item_count });
          announce(outcome.message, outcome.status === 'needs-review');
        } catch (error) {
          io.trace('native-sync-error', { outcome: outcome?.status || 'unknown' });
          const reason = outcome?.status === 'needs-review' ? `${outcome.message} ` : '';
          announce(`${reason}Your cart could not be refreshed. Review the updated cart before checkout.`, true);
        } finally {
          setBusy(false);
          schedule();
        }
      });
    }
    function removeControls(row) {
      row.removeAttribute('data-rf-pen-ready');
      row.querySelector('[data-rf-pen-shades]')?.remove();
    }
    function render() {
      if (stopped || busy) return;
      doc.querySelectorAll('#kaching-cart .kaching-cart-item__info').forEach(info => {
        const nativeRows = nativePenRows(info);
        if (!nativeRows.length) {
          info.removeAttribute('data-rf-native-pen-layout');
          return;
        }
        info.setAttribute('data-rf-native-pen-layout', String(nativeRows.length));
        nativeRows.forEach((row, index) => {
          const select = row.querySelector('select');
          const label = `Brow Rafiner pen ${index + 1} of ${nativeRows.length}, shade`;
          if (select.getAttribute('aria-label') !== label) select.setAttribute('aria-label', label);
        });
      });
      const api = win.kachingCartApi;
      if (!api?.refreshCart || !api.getCartData || !installCheckoutGuard()) return;
      cart = api.getCartData() || cart;
      if (!cart?.items) return;
      const rows = doc.querySelectorAll(rowsSelector);
      if (!variants && [...rows].some(row => multiPen(cart.items.find(item => item.key === row.dataset.lineKey)))) {
        loadProduct();
        return;
      }
      rows.forEach(row => {
        const item = cart.items.find(entry => entry.key === row.dataset.lineKey);
        const native = row.querySelector(nativeSelector);
        if (row.matches('.kaching-cart-item--bundle,.kaching-cart-extra-item') || !eligible(item, variants) || !variants || !native ||
          !variants.some(variant => String(variant.id) === String(item.variant_id))) {
          removeControls(row);
          return;
        }
        const choices = choicesFor(item, variants);
        const signature = `${item.key}|${item.quantity}|${planId(item) || ''}|${choices.map(variant => `${variant.id}:${variant.available}`).join(',')}`;
        let controls = row.querySelector('[data-rf-pen-shades]');
        if (controls?.dataset.signature !== signature) {
          controls?.remove();
          controls = doc.createElement('div');
          controls.className = 'rf-cart-pen-shades';
          controls.setAttribute('data-rf-pen-shades', '');
          controls.dataset.signature = signature;
          controls.setAttribute('role', 'group');
          controls.setAttribute('aria-label', 'Choose a shade for each Brow Rafiner');
          for (let index = 0; index < item.quantity; index++) {
            const select = doc.createElement('select');
            select.className = 'kaching-cart-selector rf-cart-pen-shade';
            select.setAttribute('data-rf-pen-select', '');
            select.setAttribute('aria-label', `Brow Rafiner pen ${index + 1} of ${item.quantity}, shade`);
            choices.forEach(variant => {
              const option = doc.createElement('option');
              option.value = String(variant.id);
              option.textContent = variant.title + (variant.available ? '' : ' — sold out');
              option.disabled = !variant.available;
              select.append(option);
            });
            select.value = String(item.variant_id);
            select.addEventListener('change', () => {
              if (!busy && !reviewRequired && !checkoutPending && !nativeBusy(row)) changeShade(select, item);
              else select.value = String(item.variant_id);
            });
            controls.append(select);
          }
          native.after(controls);
        }
        controls.querySelectorAll('select').forEach(select => {
          select.value = String(item.variant_id);
          const disabled = reviewRequired || checkoutPending || nativeBusy(row);
          if (select.disabled !== disabled) select.disabled = disabled;
        });
        row.setAttribute('data-rf-pen-ready', '');
      });
      renderMessage();
    }
    function schedule() {
      if (scheduled || stopped) return;
      scheduled = true;
      win.requestAnimationFrame(() => { scheduled = false; render(); });
    }
    function cartUpdated(event) {
      if (!busy && event.detail?.cart) cart = event.detail.cart;
      schedule();
    }
    const observer = new win.MutationObserver(schedule);
    observer.observe(doc.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-line-key', 'class'] });
    ['click', 'input', 'change', 'submit', 'keydown'].forEach(name => win.addEventListener(name, guardInteraction, true));
    doc.addEventListener('kaching-cart:cart-updated', cartUpdated);
    doc.addEventListener('kaching-cart:open', schedule);
    win.addEventListener('pageshow', () => { checkoutPending = false; schedule(); });
    schedule();
    return { refresh: schedule, get busy() { return busy; }, get reviewRequired() { return reviewRequired; } };
  }

  return { PRODUCT_ID, identity, count, eligible, choicesFor, makePlan, stateOf, isCart, penState, synchronizeCart, nativePenRows, replaceOne, createQueue, shouldBlockInteraction, mount };
});
